import { useEffect, useRef } from 'react';

interface DetectionOptions {
  onLoadDetected: () => void;
  enabled?: boolean;
  sensitivity?: 'aggressive' | 'balanced' | 'conservative';
}

export function usePageLoadDetection({
  onLoadDetected,
  enabled = true,
  sensitivity = 'balanced'
}: DetectionOptions) {
  const hasDetectedRef = useRef(false);
  const detectionScoresRef = useRef({
    domStable: false,
    idleDetected: false,
    imagesLoaded: false,
    reactRendered: false,
    noRecentActivity: false
  });

  const lastActivityRef = useRef(Date.now());
  const timeoutIdsRef = useRef<NodeJS.Timeout[]>([]);

  const getSensitivityConfig = () => {
    switch (sensitivity) {
      case 'aggressive':
        return { requiredSignals: 2, domStabilityDelay: 300, inactivityThreshold: 500 };
      case 'conservative':
        return { requiredSignals: 4, domStabilityDelay: 1000, inactivityThreshold: 2000 };
      default:
        return { requiredSignals: 3, domStabilityDelay: 500, inactivityThreshold: 1000 };
    }
  };

  const isLoadingStatePresent = () => {
    const loadingIndicators = document.querySelectorAll('.animate-spin, .animate-pulse');
    const hasLoadingText = document.body.textContent?.toLowerCase().includes('loading');
    return loadingIndicators.length > 0 && hasLoadingText;
  };

  const hasSubstantialContent = () => {
    const contentSelectors = [
      'table tbody tr',
      '[role="table"]',
      '.grid > div',
      'main > div > div:not(.min-h-\\[50vh\\])',
      'article',
      'section:not(:empty)'
    ];

    for (const selector of contentSelectors) {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 2) {
        return true;
      }
    }
    return false;
  };

  const checkDetection = () => {
    if (hasDetectedRef.current) return;

    if (isLoadingStatePresent()) {
      console.log('[PageLoadDetection] LoadingState still present, not triggering detection');
      return;
    }

    const scores = detectionScoresRef.current;
    const signalsDetected = Object.values(scores).filter(Boolean).length;
    const config = getSensitivityConfig();

    if (signalsDetected >= config.requiredSignals && hasSubstantialContent()) {
      hasDetectedRef.current = true;
      console.log('[PageLoadDetection] Load detected with signals:', scores);
      onLoadDetected();
    }
  };

  useEffect(() => {
    if (!enabled) return;

    const config = getSensitivityConfig();
    let mutationTimeout: NodeJS.Timeout | null = null;
    let observer: MutationObserver | null = null;

    detectionScoresRef.current.reactRendered = true;
    checkDetection();

    observer = new MutationObserver(() => {
      lastActivityRef.current = Date.now();

      if (mutationTimeout) {
        clearTimeout(mutationTimeout);
      }

      mutationTimeout = setTimeout(() => {
        if (!hasDetectedRef.current) {
          detectionScoresRef.current.domStable = true;
          checkDetection();
        }
      }, config.domStabilityDelay);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    });

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        if (!hasDetectedRef.current) {
          detectionScoresRef.current.idleDetected = true;
          checkDetection();
        }
      }, { timeout: 2000 });
    } else {
      const idleTimeout = setTimeout(() => {
        if (!hasDetectedRef.current) {
          detectionScoresRef.current.idleDetected = true;
          checkDetection();
        }
      }, 1000);
      timeoutIdsRef.current.push(idleTimeout);
    }

    const checkImages = () => {
      const images = Array.from(document.images);
      const allLoaded = images.length === 0 || images.every(img => img.complete);

      if (allLoaded && !hasDetectedRef.current) {
        detectionScoresRef.current.imagesLoaded = true;
        checkDetection();
      } else if (images.length > 0) {
        Promise.all(
          images.map(img => {
            if (img.complete) return Promise.resolve();
            return new Promise(resolve => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            });
          })
        ).then(() => {
          if (!hasDetectedRef.current) {
            detectionScoresRef.current.imagesLoaded = true;
            checkDetection();
          }
        });
      }
    };

    const imageCheckTimeout = setTimeout(checkImages, 500);
    timeoutIdsRef.current.push(imageCheckTimeout);

    const inactivityCheckInterval = setInterval(() => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      if (timeSinceActivity > config.inactivityThreshold && !hasDetectedRef.current) {
        detectionScoresRef.current.noRecentActivity = true;
        checkDetection();
      }
    }, 500);

    const contentCheckInterval = setInterval(() => {
      if (!hasDetectedRef.current && !isLoadingStatePresent() && hasSubstantialContent()) {
        console.log('[PageLoadDetection] Content detected, LoadingState removed');
        hasDetectedRef.current = true;
        onLoadDetected();
      }
    }, 1000);

    const fallbackTimeout = setTimeout(() => {
      if (!hasDetectedRef.current && !isLoadingStatePresent()) {
        console.log('[PageLoadDetection] Fallback timeout triggered after 60s');
        hasDetectedRef.current = true;
        onLoadDetected();
      }
    }, 60000);
    timeoutIdsRef.current.push(fallbackTimeout);

    return () => {
      if (observer) {
        observer.disconnect();
      }
      if (mutationTimeout) {
        clearTimeout(mutationTimeout);
      }
      clearInterval(inactivityCheckInterval);
      clearInterval(contentCheckInterval);
      timeoutIdsRef.current.forEach(id => clearTimeout(id));
      timeoutIdsRef.current = [];
    };
  }, [enabled, sensitivity, onLoadDetected]);

  return {
    isDetected: hasDetectedRef.current
  };
}
